
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Avatar,
    IconButton,
    Menu,
    MenuItem,
    useTheme,
    useMediaQuery,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Grid,
    Card,
    CardContent
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    PostAdd as PostAddIcon,
    Article as ArticleIcon,
    People as PeopleIcon,
    ShoppingBag as ShoppingBagIcon,
    Logout as LogoutIcon,
    KeyboardArrowDown as ArrowDownIcon,
    Person as PersonIcon,
    TrendingUp as TrendingUpIcon,
    Favorite as FavoriteIcon,
    Comment as CommentIcon,
    Assignment as AssignmentIcon,
    LocalOffer as LocalOfferIcon,
    MoreHoriz as MoreIcon,
    Percent as PercentIcon,
    Close as CloseIcon,

} from '@mui/icons-material';
import { ReceiptLong as OrdersIcon } from '@mui/icons-material';





import Logo from '../assets/Logo.png';
import '../styles/ProtectedRoutes.css';

const DRAWER_WIDTH = 240;

// Full list — used for the desktop sidebar (all items shown)
const navigationItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { label: 'Create Post', icon: <PostAddIcon />, path: '/admin/create-post' },
    { label: 'Requests', icon: <AssignmentIcon />, path: '/admin/requests' },
    { label: 'Customers', icon: <PeopleIcon />, path: '/admin/customers' },
    { label: 'Products', icon: <ShoppingBagIcon />, path: '/admin/products' },
    { label: 'Coupons', icon: <LocalOfferIcon />, path: '/admin/coupons' },
    { label: 'Orders', icon: <OrdersIcon />, path: '/admin/orders' },

    { label: 'Commission', icon: <PercentIcon />, path: '/admin/commission' },
];

// Mobile bottom nav — the most-used 5 show directly
const primaryNavItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { label: 'Requests', icon: <AssignmentIcon />, path: '/admin/requests' },
    { label: 'Customers', icon: <PeopleIcon />, path: '/admin/customers' },
    { label: 'Products', icon: <ShoppingBagIcon />, path: '/admin/products' },
    { label: 'Orders', icon: <OrdersIcon />, path: '/admin/orders' },
];

// Mobile bottom nav — the rest live behind "More"
const moreNavItems = [
    { label: 'Create Post', icon: <PostAddIcon />, path: '/admin/create-post' },
    { label: 'Coupons', icon: <LocalOfferIcon />, path: '/admin/coupons' },
    { label: 'Commission', icon: <PercentIcon />, path: '/admin/commission' },
];

const AdminDashboard = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            toast.error('Logout failed, but you will be redirected to login.');
            navigate('/login');
        }
    };

    const handleLogoutClick = () => {
        setAnchorEl(null);
        setLogoutDialogOpen(true);
    };

    const handleLogoutConfirm = () => {
        setLogoutDialogOpen(false);
        handleLogout();
    };

    const handleLogoutCancel = () => {
        setLogoutDialogOpen(false);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Index for the BottomNavigation `value` prop.
    // 0-3 = primary tabs, 4 = "More" tab (highlighted if current path is one of the "more" items)
    const getCurrentTabIndex = () => {
        const currentPath = location.pathname;
        const primaryIndex = primaryNavItems.findIndex(item => item.path === currentPath);
        if (primaryIndex >= 0) return primaryIndex;
        if (moreNavItems.some(item => item.path === currentPath)) return primaryNavItems.length;
        return 0;
    };

    const handleNavigation = (path) => {
        navigate(path);
    };

    const handleMoreClick = (event) => {
        setMoreMenuAnchor(event.currentTarget);
    };

    const handleMoreClose = () => {
        setMoreMenuAnchor(null);
    };

    const handleMoreItemClick = (path) => {
        setMoreMenuAnchor(null);
        navigate(path);
    };

    return (
        <Box className="protected-container" sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Desktop Sidebar */}
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                        },
                    }}
                    classes={{
                        paper: 'glass-sidebar'
                    }}
                >
                    {/* Sidebar Header */}
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={Logo}
                            alt="Ginni Fashion Studio"
                            className="glass-avatar"
                            sx={{ width: 40, height: 40 }}
                        />
                        <Box>
                            <Typography variant="subtitle1" className="glass-title" sx={{ lineHeight: 1.2 }}>
                                Admin Panel
                            </Typography>
                            <Typography variant="caption" className="glass-subtitle" sx={{ lineHeight: 1.2 }}>
                                Ginni Fashion
                            </Typography>
                        </Box>
                    </Box>

                    <Divider className="glass-divider" />

                    {/* Navigation Items — full list on desktop */}
                    <List sx={{ flexGrow: 1, pt: 1, px: 1 }}>
                        {navigationItems.map((item, index) => (
                            <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                    onClick={() => handleNavigation(item.path)}
                                    className={location.pathname === item.path ? 'nav-item-selected' : 'nav-item-hover'}
                                    sx={{
                                        mx: 1,
                                        borderRadius: 2,
                                        py: 1,
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        slotProps={{
                                            primary: {
                                                fontWeight: location.pathname === item.path ? 600 : 400,
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>

                    <Divider className="glass-divider" />

                    {/* Logout Button */}
                    <Box sx={{ p: 2 }}>
                        <Button
                            fullWidth
                            className="glass-button-outlined"
                            startIcon={<LogoutIcon />}
                            onClick={handleLogoutClick}
                            size="small"
                        >
                            Logout
                        </Button>
                    </Box>
                </Drawer>
            )}

            {/* Main Content Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}>
                {/* Top App Bar */}
                <AppBar position="fixed" elevation={1} className="glass-appbar" sx={{ top: 0, zIndex: 1100, left: isMobile ? 0 : DRAWER_WIDTH }}>
                    <Toolbar>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                            {isMobile && (
                                <Avatar
                                    src={Logo}
                                    alt="Ginni Fashion Studio"
                                    className="glass-avatar"
                                    sx={{ width: 40, height: 40, mr: 2 }}
                                />
                            )}
                            <Typography
                                variant={isMobile ? "h6" : "h6"}
                                component="div"
                                className="glass-title"
                                sx={{
                                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                                }}
                            >
                                {isMobile ? 'Admin Panel' : 'Ginni Fashion Studio - Admin Panel'}
                            </Typography>
                        </Box>

                        {/* User Info Section */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? 0.5 : 1
                        }}>
                            <IconButton onClick={handleMenuOpen} size="large" className="glass-icon-button" sx={{ p: 0 }}>
                                <Avatar className="glass-avatar" sx={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32 }}>
                                    {(user?.fullName || user?.name || user?.email)?.charAt(0)?.toUpperCase()}
                                </Avatar>
                            </IconButton>

                            <Typography
                                variant="body2"
                                className="glass-text-primary"
                                sx={{
                                    fontWeight: 500,
                                    mr: 0.5,
                                    display: { xs: 'none', sm: isMobile ? 'block' : 'block', md: 'block' },
                                    fontSize: isMobile ? '0.875rem' : '0.875rem',
                                    maxWidth: isMobile ? '120px' : 'none',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {user?.fullName || user?.name || user?.email}
                            </Typography>

                            <IconButton onClick={handleMenuOpen} size="small" className="glass-icon-button">
                                <ArrowDownIcon sx={{
                                    color: '#bc965e',
                                    fontSize: isMobile ? 18 : 20
                                }} />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* User Menu Dropdown */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    classes={{
                        paper: 'glass-menu'
                    }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/profile'); }} className="glass-menu-item">
                        <PersonIcon sx={{ mr: 1, color: '#bc965e' }} />
                        <Typography className="glass-text-primary">View Profile</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleLogoutClick} className="glass-menu-item">
                        <LogoutIcon sx={{ mr: 1, color: '#bc965e' }} />
                        <Typography className="glass-text-primary">Logout</Typography>
                    </MenuItem>
                </Menu>

                {/* Logout Confirmation Dialog */}
                <Dialog
                    open={logoutDialogOpen}
                    onClose={handleLogoutCancel}
                    aria-labelledby="logout-dialog-title"
                    aria-describedby="logout-dialog-description"
                >
                    <DialogTitle id="logout-dialog-title">
                        Confirm Logout
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="logout-dialog-description">
                            Are you sure you want to logout? You will need to sign in again to access the admin panel.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleLogoutCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleLogoutConfirm} color="primary" variant="contained">
                            Logout
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Main Content */}
                <Box
                    component="main"
                    className="protected-content"
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        height: isMobile ? 'calc(100vh - 64px - 64px)' : 'calc(100vh - 64px)',
                        mt: '64px',
                        mb: isMobile ? '64px' : 0,
                    }}
                >
                    {children}
                </Box>

                {/* Bottom Navigation for Mobile */}
                {isMobile && (
                    <Paper className="glass-bottom-nav" sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={3}>
                        <BottomNavigation
                            value={getCurrentTabIndex()}
                            onChange={(_, newValue) => {
                                if (newValue === primaryNavItems.length) {
                                    // "More" tab — handled by its own onClick below via handleMoreClick,
                                    // but BottomNavigation still fires onChange, so just guard here.
                                    return;
                                }
                                const item = primaryNavItems[newValue];
                                if (item) {
                                    handleNavigation(item.path);
                                }
                            }}
                            showLabels
                            sx={{
                                backgroundColor: 'transparent',
                                height: '64px',
                                '& .MuiBottomNavigationAction-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    minWidth: 0,
                                    maxWidth: 'none',
                                    padding: '6px 2px',
                                    '& .MuiBottomNavigationAction-label': {
                                        fontSize: '0.65rem',
                                        lineHeight: 1.1,
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        transition: 'none',
                                    },
                                    '&.Mui-selected': {
                                        color: '#bc965e',
                                        '& .MuiBottomNavigationAction-label': {
                                            fontSize: '0.65rem',
                                        }
                                    }
                                }
                            }}
                        >
                            {primaryNavItems.map((item, index) => (
                                <BottomNavigationAction
                                    key={index}
                                    label={item.label}
                                    icon={item.icon}
                                />
                            ))}
                            <BottomNavigationAction
                                label="More"
                                icon={<MoreIcon />}
                                onClick={handleMoreClick}
                            />
                        </BottomNavigation>
                    </Paper>
                )}

                {/* "More" menu for mobile bottom nav overflow items */}
                <Menu
                    anchorEl={moreMenuAnchor}
                    open={Boolean(moreMenuAnchor)}
                    onClose={handleMoreClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    classes={{ paper: 'glass-menu' }}
                    slotProps={{
                        list: {
                            sx: { pt: 0, pb: 1 }
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.5, mb: -1.5, }}>
                        <IconButton
                            onClick={handleMoreClose}
                            size="small"
                            sx={{ color: '#bc965e', p: 0.5 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {moreNavItems.map((item, index) => (
                        <MenuItem
                            key={index}
                            dense
                            onClick={() => handleMoreItemClick(item.path)}
                            className="glass-menu-item"
                            selected={location.pathname === item.path}
                            sx={{ py: 1 }}
                        >
                            <Box sx={{ mr: 1.5, color: '#bc965e', display: 'flex' }}>
                                {item.icon}
                            </Box>
                            <Typography className="glass-text-primary">{item.label}</Typography>
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </Box>
    );
};

export default AdminDashboard;